import { ComponentPropsWithoutRef, createContext, useContext, useMemo } from 'react';
import { Box, BoxProps } from '@givebutter/primitives/box';
import { faCircle } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { twMerge } from 'tailwind-merge';

const VerificationStepContext = createContext({
    isCompleted: false,
});

interface VerificationStepProps extends BoxProps {
    isCompleted: boolean;
}

const VerificationStep = ({
    isCompleted = false,
    className = '',
    ...rest
}: VerificationStepProps) => {
    const value = useMemo(() => ({ isCompleted }), [isCompleted]);

    return (
        <VerificationStepContext.Provider value={value}>
            <Box className={twMerge('flex flex-col gap-4 p-6', className)} {...rest} />
        </VerificationStepContext.Provider>
    );
};

interface VerificationStepHeadingProps extends ComponentPropsWithoutRef<'div'> {}
const VerificationStepHeading = ({ className = '', ...rest }: VerificationStepHeadingProps) => {
    return <div className={twMerge('flex items-center gap-4', className)} {...rest} />;
};

interface VerificationStepHeadingIconProps {
    className?: string;
    size?: FontAwesomeIconProps['size'];
}
const VerificationStepHeadingIcon = ({
    className = '',
    size = 'xl',
}: VerificationStepHeadingIconProps) => {
    const { isCompleted } = useContext(VerificationStepContext);
    return (
        <FontAwesomeIcon
            icon={isCompleted ? faCircleCheck : faCircle}
            className={twMerge('hidden text-blue-600 sm:block', className)}
            size={size}
        />
    );
};

interface VerificationStepHeadingContentProps extends ComponentPropsWithoutRef<'div'> {}
const VerificationStepHeadingContent = ({
    className = '',
    ...rest
}: VerificationStepHeadingContentProps) => {
    return <div className={twMerge('flex flex-col gap-1', className)} {...rest} />;
};

interface VerificationStepHeadingStepProps extends ComponentPropsWithoutRef<'p'> {}
const VerificationStepHeadingStep = ({
    className = '',
    ...rest
}: VerificationStepHeadingStepProps) => {
    return (
        <p
            className={twMerge('text-sm font-medium leading-none text-gray-500', className)}
            {...rest}
        />
    );
};

interface VerificationStepHeadingTitleProps extends ComponentPropsWithoutRef<'p'> {}
const VerificationStepHeadingTitle = ({
    className = '',
    ...rest
}: VerificationStepHeadingTitleProps) => {
    return <p className={twMerge('text-md font-medium leading-none', className)} {...rest} />;
};

interface VerificationStepHeadingSubtitleProps extends ComponentPropsWithoutRef<'span'> {}
const VerificationStepHeadingSubtitle = ({
    children = '',
    className = '',
    ...rest
}: VerificationStepHeadingSubtitleProps) => {
    const { isCompleted } = useContext(VerificationStepContext);

    return isCompleted && children ? (
        <>
            {' '}
            <span className={twMerge('text-gray-500', className)} {...rest}>
                &mdash; {children}
            </span>
        </>
    ) : null;
};

interface VerificationStepHeadingLabelProps extends ComponentPropsWithoutRef<'div'> {}
const VerificationStepHeadingLabel = ({
    className = '',
    ...rest
}: VerificationStepHeadingLabelProps) => {
    const { isCompleted } = useContext(VerificationStepContext);
    return isCompleted ? null : (
        <div className="ml-auto">
            <div className={twMerge('text-sm font-medium', className)} {...rest} />
        </div>
    );
};

interface VerificationStepContentProps extends ComponentPropsWithoutRef<'div'> {}
const VerificationStepContent = ({ className = '', ...rest }: VerificationStepContentProps) => {
    const { isCompleted } = useContext(VerificationStepContext);

    return isCompleted ? null : (
        <div className={twMerge('flex flex-col gap-4 sm:ml-10', className)} {...rest} />
    );
};

const Root = VerificationStep;
const Heading = VerificationStepHeading;
const HeadingIcon = VerificationStepHeadingIcon;
const HeadingContent = VerificationStepHeadingContent;
const HeadingStep = VerificationStepHeadingStep;
const HeadingTitle = VerificationStepHeadingTitle;
const HeadingSubtitle = VerificationStepHeadingSubtitle;
const HeadingLabel = VerificationStepHeadingLabel;
const Content = VerificationStepContent;

export {
    VerificationStep,
    VerificationStepHeading,
    VerificationStepHeadingIcon,
    VerificationStepHeadingContent,
    VerificationStepHeadingStep,
    VerificationStepHeadingTitle,
    VerificationStepHeadingSubtitle,
    VerificationStepHeadingLabel,
    VerificationStepContent,
    //
    Root,
    Heading,
    HeadingIcon,
    HeadingContent,
    HeadingStep,
    HeadingTitle,
    HeadingSubtitle,
    HeadingLabel,
    Content,
};

export type {
    VerificationStepProps,
    VerificationStepHeadingProps,
    VerificationStepHeadingIconProps,
    VerificationStepHeadingContentProps,
    VerificationStepHeadingStepProps,
    VerificationStepHeadingTitleProps,
    VerificationStepHeadingSubtitleProps,
    VerificationStepHeadingLabelProps,
    VerificationStepContentProps,
};
